<template>
  <div class="page">
    <!-- OVERLAY FOR RESULTS UPDATE -->
    <v-overlay
      absolute
      :value="isUpdateQuoteShow && $vuetify.breakpoint.lgAndUp"
    />

    <!-- HEADER -->
    <site-header ref="header" />

    <!-- SMALL DEVICES ADJUST OPTIONS -->
    <v-sheet
      v-if="$vuetify.breakpoint.mdAndDown"
      color="navy-medium"
      dark
      tile
      class="px-3 d-flex align-center"
    >
      <v-col>
        <v-btn
          v-if="!isQuotesLoading"
          outlined
          rounded
          small
          color="white"
          data-test="adjust_your_cover"
          @click="isOptionsDrawerVisible = !isOptionsDrawerVisible"
        >
          <v-icon
            small
            class="mr-1"
          >
            mdi-tune
          </v-icon>
          Adjust your cover
        </v-btn>
      </v-col>
      <v-col>
        <v-switch
          v-model="isViewMonthly"
          data-test="view_monthly"
          :label="isViewMonthly ? 'Monthly cost' : 'Annual cost'"
          inset
          hide-details
          class="mt-0 pt-0 payment-type-switch"
        />
      </v-col>
    </v-sheet>

    <!-- RESULTS TABLE / RESULTS FILTERS -->
    <!-- NOTE: questionSetChanged - is temporary solution for https://seopa01.atlassian.net/browse/PU-3478. Please sync with BE to refactor it after Car Question Set will be in live -->
    <v-container
      v-if="(hasSucceededQuotes || !isQuotesLoading) && !questionSetChanged"
      :fluid="$vuetify.breakpoint.lgAndDown"
      class="py-4 px-md-8"
    >
      <v-row class="my-0">
        <!-- RESULTS FILTERS -->
        <v-col
          cols="12"
          lg="3"
          class="py-0"
        >
          <v-navigation-drawer
            v-model="isOptionsDrawerVisible"
            :app="$vuetify.breakpoint.mdAndDown"
            :right="$vuetify.breakpoint.mdAndDown"
            align-self="end"
            clipped
            floating
            touchless
            :color="$vuetify.breakpoint.lgAndUp ? 'transparent' : undefined"
            :width="$vuetify.breakpoint.lgAndUp ? 400 : 360"
            :height="$vuetify.breakpoint.lgAndUp ? 'auto' : '100%'"
            :permanent="$vuetify.breakpoint.lgAndUp"
            :style="$vuetify.breakpoint.lgAndUp && `position:sticky; top: ${fixedTopIndent}px;`"
            :class="[ $vuetify.breakpoint.lgAndUp && 'fix-height',
                      isQuotesLoading && 'v-navigation-drawer-disabled',
                      isUpdateQuoteShow && 'requote-shown' ]"
          >
            <results-options
              v-if="formData"
              :form-data="formData"
              :details-description="getInitDetailsDescription"
              :options="filterOptions"
              :selected-filters="selectedFilters"
              :is-update-quote-show="isUpdateQuoteShow"
              :telematics-quantity="telematicsQuantity"
              @click-edit-quote="goToForm"
              @click-close="isOptionsDrawerVisible = !isOptionsDrawerVisible"
              @click-details="openDetailsDrawer"
              @change-filter="handleChangeFilter"
              @change-requote-filter="handleChangeRequoteFilter"
              @cancel-re-quote="cancelReQuote"
              @re-quote="reQuote"
            />
          </v-navigation-drawer>
        </v-col>

        <!-- RESULTS TABLE -->
        <v-col
          cols="12"
          lg="9"
          class="py-0 d-flex flex-column flex-grow-1"
        >
          <main
            class="flex-grow-1"
          >
            <results-table
              :quote-ref="quoteRef"
              :quotes-qty="quotesQuantity"
              :columns="resultsTableColumns"
              :insured-name="insuredName"
              :all-results-qty="allResults.length"
              :failed-quotes="failedResults"
              :is-no-results="isNoResults"
              :has-quote-summary="!!quoteSummary"
              :selected-optional-extras="selectedOptionalExtras"
              :fixed-top-indent="fixedTopIndent"
              :telematics-quantity="telematicsQuantity"
              :has-broker-results="hasBrokerResults"
              :is-broker-solution-message-shown="isBrokerSolutionMessageShown"
              :is-broker-solution-table-shown="isBrokerSolutionTableShown"
              :broker-quotes-qty="brokerShownResults.length"
              @open-drawer-details="openDetailsDrawer"
              @go-to-form="goToForm"
            >
              <!-- BROKER NOTIFICATIONS -->
              <template
                v-if="isBrokerSolutionMessageShown"
                #brokerResultsMessage
              >
                {{ config.partnerText }} prices for a
                <strong v-html="quoteVehicleDetailsMake" />
                with <strong v-html="quoteCoverDetails" /> cover for
                <strong v-html="quoteVehicleDetailsUse" /> use.
              </template>
              <!-- BROKER CUSTOM MESSAGE -->
              <template
                v-if="config.partnerMessage"
                #partnerMessage
              >
                <div v-html="config.partnerMessage" />
              </template>
              <!-- BROKER RESULTS -->
              <template
                v-if="isBrokerSolutionTableShown"
                #brokerResults
              >
                <div
                  v-for="(chosenQuote, index) of brokerShownResults"
                  :id="'chosen-quote-'+chosenQuote.id"
                  :key="'chosen-quote-'+chosenQuote.id"
                  class="quote-container mb-6"
                >
                  <transition name="fade-in">
                    <results-quote
                      v-show="chosenQuote.isVisible"
                      :quote="chosenQuote"
                      :is-view-monthly="isViewMonthly"
                      :optional-extras-columns="getOptionalExtrasValues"
                      :selected-optional-extras="optionalExtras"
                      @click-view-details="clickViewDetails($event, index)"
                      @open-drawer-details="openDetailsDrawer"
                      @toggle-quote-shown="toggleQuoteShown($event, brokerResults)"
                    />
                  </transition>
                </div>
              </template>
              <!-- QZ NOTIFICATION -->
              <template
                v-if="isQZShownMessageShown"
                #QZMessage
              >
                <template v-if="isBrokerSolutionEnabled">
                  <span v-if="hasBrokerResults">
                    In addition to our prices (shown above), we
                  </span>
                  <span v-else>We</span>
                  have selected insurance comparison experts Quotezone to help you
                  find a better deal on your insurance. They have searched their panel of insurance providers and found
                  <strong>{{ quotesQuantity }} quotes</strong> starting from <strong>£{{ shownResultsCheapestPrice }}.</strong><br><br>
                  <u>
                    Note that if you purchase  your insurance from a quote below, you will be purchasing directly with
                    that insurance provider
                  </u>
                </template>
                <template v-else>
                  <span v-if="insuredName">
                    {{ insuredName }}, we're
                  </span>
                  <span v-else>
                    We're
                  </span>
                  showing you prices for a
                  <strong v-html="quoteVehicleDetailsMake" />
                  with
                  <strong v-html="quoteCoverDetails" /> cover
                  for
                  <strong v-html="quoteVehicleDetailsUse" />
                  use.
                </template>
              </template>
              <!-- ALL RESULTS -->
              <template #quotes>
                <div
                  v-if="isQZShown"
                  key="quotes"
                >
                  <div
                    v-for="(quote, index) of shownResults"
                    :key="quote.id"
                    class="quote-container mb-6"
                  >
                    <transition name="fade-in">
                      <results-quote
                        v-show="quote.isVisible"
                        :quote="quote"
                        :is-view-monthly="isViewMonthly"
                        :optional-extras-columns="getOptionalExtrasValues"
                        :selected-optional-extras="optionalExtras"
                        @click-view-details="clickViewDetails($event, index)"
                        @open-drawer-details="openDetailsDrawer"
                        @toggle-quote-shown="toggleQuoteShown"
                      />
                    </transition>
                  </div>
                </div>
              </template>
            </results-table>
            <results-additional-quotes
              v-if="isAdditionalResultsCTAShown"
              @get-more-quotes="onBrokerSolutionModalSuccess"
            />
          </main>
        </v-col>
      </v-row>

      <!-- FOOTER -->
      <v-footer
        v-if="config.partnerFooter"
        padless
        color="transparent"
        tag="div"
      >
        <site-footer role="footer">
          <div v-html="config.partnerFooterText || ''" />
        </site-footer>
      </v-footer>

      <branding-logo
        v-if="config.brandingLogo"
        :branding-type="config.brandingType"
      />

      <!-- RESULTS FOUND SMALL MODAL -->
      <loading-notice
        :is-loading="isQuotesLoading"
        :totalcount="totalCount"
      />
    </v-container>

    <!-- RESULTS REQUOTE -->
    <results-requote
      v-if="isResultsRequoteShown"
      :redirect-url="resultsRequoteRedirectUrl"
      :partner-image="config.partnerImage"
      :partner-text="config.partnerText"
      @on-click-how-it-works="isResultsRequoteModalShown = true"
    />

    <!-- ASIDE DRAWER -->
    <v-navigation-drawer
      v-model="isDetailsDrawerOpen"
      app
      right
      temporary
      touchless
      :width="drawerWidth"
      height="100%"
    >
      <keep-alive>
        <component
          :is="drawerComponent"
          ref="drawer"
          :quote="detailsQuote"
          :quote-position-number="quotePositionNumber"
          :details-description="getDetailsDescription"
          :is-view-monthly="isViewMonthly"
          :is-details-drawer-open="isDetailsDrawerOpen"
          :selected-optional-extras="optionalExtras"
          :quote-summary="quoteSummary"
          :form-step-labels="formStepLabels"
          :product-name="productName"
          @re-quote="reQuote"
          @open-drawer-details="openDetailsDrawer"
          @go-to-form="goToForm"
          @close-drawer="closeDetailsDrawer"
        />
      </keep-alive>
    </v-navigation-drawer>

    <!-- RESULTS FAILED MODAL -->
    <results-modal-alerts
      :modal-alert="modalAlert"
      @go-to-form="goToForm"
      @new-start-date-requote="reQuote"
      @cancel="modalAlert = false"
    />

    <!-- BROKER SOLUTION EXIT ONLY MODAL -->
    <results-modal-alerts
      v-if="isBrokerSolutionModalShowed"
      ref="brokerModal"
      modal-alert="brokerModal"
      @success="onBrokerSolutionModalSuccess"
      @cancel="onBrokerSolutionModalCancel"
    />

    <!-- AUTOQUTE MODAL  -->
    <modal-autoquote
      v-if="isAutoquteModalShown"
      @click-view-quote="toggleAutoquoteModal(false)"
      @click-edit-quote="goToForm"
    />

    <!-- RESULTS LOADING MODAL -->
    <results-loading-modal
      :quoting-done-percentage="quotingDonePercentage"
      :has-succeeded-quotes="hasSucceededQuotes"
      :is-quotes-loading="isQuotesLoading"
    />

    <!-- RESULTS REQUOTE MODAL -->
    <results-requote-modal
      v-if="isResultsRequoteModalShown"
      :redirect-url="resultsRequoteRedirectUrl"
      :partner-text="config.partnerText"
      :car-sales-price="config.carSalesPrice"
      :car-sales-text="config.carSalesText"
      @on-results-requote-modal-close="isResultsRequoteModalShown = false"
    />
  </div>
</template>

<script>
import useQuotezoneCommon from '@/composables/useQuotezoneCommon';
import useQuotezoneResult from '@/composables/useQuotezoneResult';
import useBrokerSolution from '@/composables/useBrokerSolution';
import detailsDescription from '@/helpers/car/details.json';
import getStepOptions from '@/helpers/car/getStepOptions';

import ResultsOptions from '@/components/car/Results/ResultsOptions.vue';
import ResultsTable from '@/components/car/Results/ResultsTable.vue';
import ResultsDetails from '@/components/car/Results/ResultsDetails.vue';
import DetailsQuoteSummary from '@/components/car/Results/detailsDrawer/DetailsQuoteSummary.vue';
import DetailsDescription from '@/components/car/Results/detailsDrawer/DetailsDescription.vue';
import ResultsModalAlerts from '@/components/car/Results/ResultsModalAlerts.vue';
import LoadingNotice from '@/components/car/Results/ResultsLoadingNotice.vue';
import ResultsQuote from '@/components/car/Results/ResultsQuote.vue';
import ModalAutoquote from '@/components/car/Results/ModalAutoquote';
import ResultsLoadingModal from '@/components/ResultsLoadingModal';
import ResultsRequoteModal from '@/components/car/Results/ResultsRequoteModal.vue';
import ResultsRequote from '@/components/car/Results/ResultsRequote.vue';
import ResultsAdditionalQuotes from '@/components/car/Results/ResultsAdditionalQuotes.vue';

import { carStructure } from '@/helpers/products/car';

export default {
  name: 'page-car-insurance-results',
  inject: ['config', 'constants'],
  metaInfo () {
    return {
      title: `${this.config.partnerText} | Car Insurance | Quotes`,
    };
  },
  components: {
    ResultsDetails,
    ResultsOptions,
    ResultsTable,
    DetailsQuoteSummary,
    DetailsDescription,
    ResultsModalAlerts,
    LoadingNotice,
    ResultsQuote,
    ModalAutoquote,
    ResultsLoadingModal,
    ResultsRequote,
    ResultsRequoteModal,
    ResultsAdditionalQuotes,
  },
  data () {
    return {
      // Pass to DetailsList, based on data get step number
      // when click edit details
      formStepLabels: {
        'Vehicle Details': '1',
        'About You': '2',
        'Cover Details': '3',
      },
    };
  },
  computed: {
    resultsTableColumns () {
      return [
        {
          name: this.isViewMonthly ? 'Annually' : 'Provider',
        },
        {
          name: this.isViewMonthly ? 'Monthly' : 'Annually',
        },
        {
          name: 'Excess',
        },
        {
          name: 'Windscreen cover',
        },
        {
          name: 'Courtesy car',
        },
        {
          name: 'Breakdown cover',
        },
        {
          name: 'Personal accident',
        },
        {
          name: 'Legal cover',
        },
        {
          name: 'Policy details',
        },
      ];
    },
    filterOptions () {
      return {
        optionalExtrasOptions: this.constants.OPTIONAL_EXTRAS_OPTIONS,
        typeOfCoverOptions: this.getStepOptions(3).typeOfCover,
        voluntaryExcessOptions: this.getStepOptions(3).voluntaryExcess,
        telematicsOptions: this.constants.TELEMATICS_OPTIONS,
      };
    },
    selectedFilters () {
      return {
        isViewMonthly: this.isViewMonthly,
        optionalExtras: this.optionalExtras,
        telematics: this.telematics,
      };
    },
    getOptionalExtrasValues () {
      return this.filterOptions.optionalExtrasOptions.map(option => option.value);
    },
    telematicsQuantity () {
      return this.allResults?.filter((x) => x.details[0]?.telematicsDetails?.isTelematics === 'Y').length;
    },
  },
  async created () {
    this.initSelectedFilters();
    await this.getQuotes();
    if (this.isBrokerSolutionEnabled) {
      this.initExitOnlyModal();
    }
  },
  methods: {
    getStepOptions,
    handleChangeFilter: function ({ filter, value }) {
      this.changeFilter({ filter, value }, this.selectedFilters);
    },
    handleChangeRequoteFilter: function ({ filter, val }) {
      if (this.formData.cover[filter] === val) return;
      this.formData.cover[filter] = val;
      this.isUpdateQuoteShow = true;
    },
  },
  setup (props, context) {
    const stepIds = carStructure.map(step => step.stepId);
    const common = useQuotezoneCommon(null, context);
    const result = useQuotezoneResult({
      stepIds,
      detailsDescription,
      gtmTracking: common.gtmTracking,
    }, context);
    const broker = useBrokerSolution(result, context);

    return {
      ...common,
      ...result,
      ...broker,
    };
  },
};
</script>

<style lang="scss" src='@/assets/scss/results/core-component.scss'></style>
